import React, { useState } from 'react';
import TailNumberInput from '@components/forms/TailNumberInput';
import { apiClient } from '@/lib/api';

/**
 * MultipleTailNumberSearch Component
 * Manages multiple tail number inputs with autocomplete, validation, and search functionality
 *
 * @param {Object} props
 * @param {Function} props.onResults - Callback when search results are available
 */
const MultipleTailNumberSearch = ({ onResults }) => {
  const MAX_FIELDS = 10;
  const [fields, setFields] = useState([{ id: 1, value: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);

  // Get valid tail numbers (non-empty, trimmed)
  const getValidTailNumbers = () => {
    return fields
      .map(field => field.value.trim())
      .filter(value => value.length > 0);
  };

  // Get unique valid tail numbers
  const getUniqueValidTailNumbers = () => {
    const validTails = getValidTailNumbers();
    return [...new Set(validTails)];
  };

  // Check if a tail number is duplicate
  const isDuplicate = (value, currentFieldId) => {
    if (!value.trim()) return false;

    const occurrences = fields.filter(
      field => field.value.trim() === value.trim() && field.id !== currentFieldId
    );

    return occurrences.length > 0;
  };

  // Add a new field
  const handleAddField = () => {
    if (fields.length < MAX_FIELDS) {
      const newId = Math.max(...fields.map(f => f.id)) + 1;
      setFields([...fields, { id: newId, value: '' }]);
    }
  };

  // Remove a field
  const handleRemoveField = (fieldId) => {
    if (fields.length > 1) {
      setFields(fields.filter(field => field.id !== fieldId));
    }
  };

  // Update field value
  const handleFieldChange = (fieldId, value) => {
    setFields(fields.map(field =>
      field.id === fieldId ? { ...field, value } : field
    ));
  };

  // Handle search
  const handleSearch = async () => {
    const uniqueTailNumbers = getUniqueValidTailNumbers();

    // Validation: at least one non-empty field
    if (uniqueTailNumbers.length === 0) {
      setError('Please enter at least one tail number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResults([]);

      // Search for each unique tail number
      const searchPromises = uniqueTailNumbers.map(tailNumber =>
        apiClient.searchAircraftByTail(tailNumber)
          .then(result => ({ tailNumber, result, success: true }))
          .catch(error => ({ tailNumber, error: error.message, success: false }))
      );

      const searchResults = await Promise.all(searchPromises);
      setResults(searchResults);

      // Notify parent component
      if (onResults) {
        onResults(searchResults);
      }

      setLoading(false);
    } catch (error) {
      console.error('Search error:', error);
      setError('Error performing search');
      setLoading(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    setFields([{ id: 1, value: '' }]);
    setResults([]);
    setError('');
  };

  // Get statistics
  const uniqueValidCount = getUniqueValidTailNumbers().length;
  const canAddMore = fields.length < MAX_FIELDS;

  return (
    <div className="multiple-tail-search-container">
      <div className="tail-search-header">
        <h2>Tail Number Search</h2>
        <p className="tail-search-subtitle">
          Search by aircraft registration number (N-Number) to find operator and certification info
        </p>
      </div>

      <div className="tail-inputs-section">
        <div className="tail-inputs-list">
          {fields.map((field, index) => (
            <TailNumberInput
              key={field.id}
              value={field.value}
              onChange={(value) => handleFieldChange(field.id, value)}
              onRemove={() => handleRemoveField(field.id)}
              showRemove={fields.length > 1}
              index={index + 1}
              placeholder={
                index === 0
                  ? 'Enter tail number (e.g., N12345)'
                  : `Enter additional tail number`
              }
              isDuplicate={isDuplicate(field.value, field.id)}
            />
          ))}
        </div>

        <button
          className="add-field-btn"
          onClick={handleAddField}
          disabled={!canAddMore}
          title={canAddMore ? 'Add another tail number field' : 'Maximum 10 fields allowed'}
        >
          + Add Another Tail Number
        </button>

        {!canAddMore && (
          <div className="max-fields-notice">
            Maximum of {MAX_FIELDS} fields reached
          </div>
        )}
      </div>

      <div className="search-actions">
        <div className="search-stats">
          {uniqueValidCount > 0 && (
            <span className="valid-count">
              {uniqueValidCount} valid {uniqueValidCount === 1 ? 'entry' : 'entries'}
            </span>
          )}
        </div>

        <div className="action-buttons">
          <button
            className="search-btn"
            onClick={handleSearch}
            disabled={loading || uniqueValidCount === 0}
          >
            {loading ? 'Searching...' : `Search ${uniqueValidCount} Aircraft`}
          </button>
          <button
            className="clear-btn"
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div className="search-error">
          {error}
        </div>
      )}

      {loading && (
        <div className="search-loading">
          Searching for {uniqueValidCount} aircraft...
        </div>
      )}

      {results.length > 0 && !loading && (
        <div className="search-results">
          <h3>Search Results ({results.length})</h3>
          {results.map((result, index) => (
            <div
              key={index}
              className={`result-card ${result.success ? 'success' : 'error'}`}
            >
              <div className="result-header">
                <h4>
                  {result.tailNumber}
                  {result.success && (
                    <span className="success-badge">Found</span>
                  )}
                  {!result.success && (
                    <span className="error-badge">Not Found</span>
                  )}
                </h4>
              </div>

              {result.success && result.result && (
                <div className="result-details">
                  <pre>{JSON.stringify(result.result, null, 2)}</pre>
                </div>
              )}

              {!result.success && (
                <div className="result-error">
                  {result.error || 'Aircraft not found'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultipleTailNumberSearch;
